import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { ReferralManagementApi, userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import { TrimText } from "../../utils/helpers";

export default function UserList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalReferrals, setTotalReferrals] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await ReferralManagementApi.getAll(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
          
          // Calculate statistics
          if (res?.data?.data?.result) {
            const users = res.data.data.result;
            setTotalUsers(users.length);
            
            const totalRefs = users.reduce((sum, user) => sum + (user.referredUsersCount || 0), 0);
            setTotalReferrals(totalRefs);
            
            const totalCreds = users.reduce((sum, user) => sum + (user.signUpBonusCredits || 0), 0);
            setTotalCredits(totalCreds);
          }
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchUsers(query), 400),
    [currentPage, pageSize, status]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };

  // Initialize currentPage and viewMode from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
  }, [searchParams]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await userServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    fetchUsers(searchItem);
  }, [currentPage, pageSize, status, refresh]);

  const renderUserCard = (user, index) => (
    <Zoom in={!isLoading} className={`user-card-zoom user-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={user._id}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 161, 221, 0.2)',
            }
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                boxShadow: '0 4px 16px rgba(0, 161, 221, 0.3)'
              }}
            >
              <PersonAddIcon sx={{ fontSize: 24, color: 'white' }} />
            </Avatar>
            <Chip
              label={`${user.referredUsersCount || 0} refs`}
              size="small"
              sx={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: '#4caf50',
                fontWeight: 600,
                border: '1px solid',
                borderColor: 'rgba(76, 175, 80, 0.3)'
              }}
            />
          </Stack>

          {/* Content */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
              @{user.username || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {user.email || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {user.phoneNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Code: {user.referralCode || 'N/A'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Credits: {user.signUpBonusCredits || 0}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
              Created: {fDate(user.createdAt)}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/referral-management/referral-user/${user._id}`)}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                }
              }}
            >
              View Referrals
            </Button>
          </Stack>
        </Paper>
      </Grid>
    </Zoom>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px"
    },
    {
      name: "User Name",
      sortable: true,
      selector: (row) => row?.username || '-',
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email || '-',
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber || '-',
    },
    {
      name: "Referral Code",
      sortable: true,
      selector: (row) => row?.referralCode || '-',
      width: "200px"
    },
    {
      name: "Credit Points",
      sortable: true,
      selector: (row) => row.signUpBonusCredits || 0,
    },
    {
      name: "Referrals",
      sortable: true,
      selector: (row) => row.referredUsersCount || 0,
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          <Tooltip title="View Referrals" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/referral-management/referral-user/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                    boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    Referral Management
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Manage and monitor user referrals and bonus credits
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Statistics Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    {totalUsers}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Users
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    {totalReferrals}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Referrals
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    {totalCredits}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Total Credits
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                <Input
                  value={searchItem}
                  placeholder="Search users..."
                  onChange={handleSearchChange}
                  sx={{
                    color: 'white',
                    '& .MuiInput-input': {
                      color: 'white',
                    },
                    '& .MuiInput-input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                    },
                    '& .MuiInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      padding: '8px 16px',
                    }
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          setSearchItem("");
                          fetchUsers("");
                        }}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box sx={{ p: 3 }}>
                {isLoading || !loaderDelay ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 3,
                        background: 'linear-gradient(135deg, #9c27b0 0%, #e91e63 100%)',
                        boxShadow: '0 8px 32px rgba(156, 39, 176, 0.3)'
                      }}
                    >
                      <PersonAddIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {data.map((user, index) => renderUserCard(user, index))}
                    </Grid>
                    
                    {/* Pagination Controls (Grid View) */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      gap: 2,
                      flexWrap: 'wrap',
                      mt: 4,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {/* Center - Item Range Display */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {totalPages > 0 ? (
                            `Showing ${((currentPage || 1) - 1) * pageSize + 1} to ${Math.min((currentPage || 1) * pageSize, totalPages)} of ${totalPages} users`
                          ) : (
                            'No users found'
                          )}
                        </Typography>
                      </Box>
                      {/* Left side - Page Size Selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                          Items per page:
                        </Typography>
                        <FormControl size="small">
                          <Select
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(e.target.value);
                              setCurrentPage(1);
                              const newSearchParams = new URLSearchParams(searchParams);
                              newSearchParams.set('pageSize', e.target.value);
                              newSearchParams.set('page', '1');
                              setSearchParams(newSearchParams);
                            }}
                            displayEmpty
                            sx={{
                              color: 'white',
                              minWidth: 80,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255, 255, 255, 0.7)',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'white',
                              },
                            }}
                          >
                            <MenuItem value={2}>2</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Right side - Navigation Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) === 1}
                          onClick={() => handlePageChange((currentPage || 1) - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 1 }}>
                          {totalPages > 0 ? (
                            `Page ${currentPage || 1} of ${Math.ceil(totalPages / pageSize)}`
                          ) : (
                            'No data'
                          )}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={totalPages === 0 || (currentPage || 1) >= Math.ceil(totalPages / pageSize)}
                          onClick={() => handlePageChange((currentPage || 1) + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* List View */
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: 'black'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        }
                      }
                    },
                    cells: {
                      style: {
                        color: 'white',
                      }
                    }
                  }}
                  columns={columns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={totalPages || 0}
                  paginationCurrentPage={currentPage || 1}
                  paginationDefaultPage={currentPage || 1}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage) =>
                    setPageSize(currentRowsPerPage)
                  }
                  paginationComponentOptions={{
                    rowsPerPageText: 'Rows per page:',
                    rangeSeparatorText: 'of',
                    selectAllRowsItem: true,
                    selectAllRowsItemText: 'All',
                  }}
                  progressPending={isLoading || !loaderDelay}
                  progressComponent={<Loader />}
                  className="custom"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>
    </Container>
  );
}
